import LockClockIcon from '@mui/icons-material/LockClock';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import React from 'react';
import { pageSurface } from './alliance-surface';
import { SYSTEM_TONES } from './alliance-tokens';

/**
 * Shown over the current page when the session can't be refreshed (Amplify
 * Hub `tokenRefresh_failure`). Not dismissible by clicking outside or
 * Escape; "Cancel" is called "Sign out" because that's what it does.
 *
 * @param open - The session has expired
 * @param onSignIn - Back to the Hosted UI
 * @param onSignOut - Clear the session
 */
export function SessionExpiredDialog(props: {
  open: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  const c = pageSurface({ inShell: true });
  const tone = SYSTEM_TONES[c.dark ? 'dark' : 'light'].info;

  return (
    <Dialog
      open={props.open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-body"
      slotProps={{ backdrop: { sx: { backgroundColor: '#00000080' } } }}
      PaperProps={{
        sx: {
          borderRadius: '14px',
          border: `1px solid ${c.border}`,
          backgroundColor: c.paper,
          backgroundImage: 'none',
          color: c.text,
          boxShadow: '0 24px 64px #00000059',
          p: 3,
          m: 2,
          width: 'calc(100% - 32px)',
          maxWidth: 420,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          letterSpacing: 'normal',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          backgroundColor: tone.bg,
          color: tone.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LockClockIcon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          id="session-expired-title"
          component="h2"
          sx={{ m: 0, fontSize: 20, fontWeight: 500 }}
        >
          Your session has expired
        </Box>
        <Box
          id="session-expired-body"
          sx={{ fontSize: 15, lineHeight: 1.5, color: c.text2 }}
        >
          For your security, you were signed out after a period of inactivity.
          Sign in again to pick up where you left off.
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
        <ButtonBase
          onClick={props.onSignOut}
          sx={{
            fontFamily: 'inherit',
            height: 40,
            px: 2,
            borderRadius: '10px',
            fontSize: 14,
            fontWeight: 500,
            color: c.text,
            '&:hover': { backgroundColor: c.hover },
          }}
        >
          Sign out
        </ButtonBase>
        <ButtonBase
          autoFocus
          onClick={props.onSignIn}
          sx={{
            fontFamily: 'inherit',
            height: 40,
            px: '18px',
            borderRadius: '10px',
            fontSize: 14,
            fontWeight: 500,
            backgroundColor: c.primary.bg,
            color: c.primary.fg,
            '&:hover': { filter: 'brightness(1.08)' },
          }}
        >
          Sign in
        </ButtonBase>
      </Box>
    </Dialog>
  );
}
